import SoftooMap from "../Map";
import Map from "ol/Map";
import { Geometry, Point, MultiPoint, LineString,
         Polygon, MultiLineString, MultiPolygon } from "ol/geom";
import LinearRing from "ol/geom/LinearRing";
import GeometryCollection from "ol/geom/GeometryCollection";

import { Drawer } from "./Drawer";
import Feature from "ol/Feature";
import VectorSource from "ol/source/Vector";
import * as jsts from "jsts";
import { Extent, ISplitResult } from "../types";

interface ISplitGeom {
  splits: Geometry[]; //jsts.geom.Geometry[];
  splits2: Geometry[]; //jsts.geom.Geometry[];
}

function ExtentsAreIntersected(extent1:Extent, extent2:Extent) : boolean {
  return extent1[0] <= extent2[2] && extent1[2] >= extent2[0] &&
         extent1[1] <= extent2[3] && extent1[3] >= extent2[1];
}

export class Splitter extends Drawer {
  private $map: SoftooMap;
  private targets: Feature[]; // features to be splitted
  private jstsOL3Parser;

  constructor(options) {
    options = options || {};
    let selectSplitTargets;
    let afterSplitted;
    if (options.source) {
      delete options.source; // we don't need to keep the drawing
    }
    if (options.selectSplitTargets) {
      selectSplitTargets = options.selectSplitTargets;
      delete options.selectSplitTargets;
    }
    if (options.afterSplitted) {
      afterSplitted = options.afterSplitted;
      delete options.afterSplitted;
    }
    super(options);

    if (selectSplitTargets) {
      this.selectSplitTargets = selectSplitTargets;
    }
    if (afterSplitted) {
      this.afterSplitted = afterSplitted;
    }
    
    this.jstsOL3Parser = new jsts.io.OL3Parser();
    this.jstsOL3Parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon, GeometryCollection);
  }

  get hasTargets() : boolean {
    return this.targets && this.targets.length>0;
  }

  // application can overwrite this function in order to select all candidates
  // from the highlighted feature, such as for RAOT to select all lands having the same num_app
  selectSplitTargets() {
    if (!this.hasTargets && this.$map.map.HighlightedFeature) {
      this.targets = [this.$map.map.HighlightedFeature];
      this.$map.selectFeature(this.$map.map.HighlightedFeature);
    }
  }

  startSplit(splitter:Feature) {
    try {
      let splitResult:ISplitResult = {
        splitter: splitter,
        targets: this.targets,
        splits: [],
        splits2: []
      }
      let splitterGeom = splitter.getGeometry();
      let jstsSplitterGeom = this.jstsOL3Parser.read(splitterGeom);
      let jstsSplitterLS = jstsSplitterGeom;
      if (splitterGeom instanceof Polygon) {
        jstsSplitterLS = this.jstsOL3Parser.read(
          new LineString(splitterGeom.getLinearRing(0).getCoordinates())
        );
      }
      this.targets.forEach((target:Feature, targetIndex) => {
        let jstsTargetGeom = this.jstsOL3Parser.read(target.getGeometry());
        let multiLineString = this.unionMultiLineStrings(jstsSplitterLS, jstsTargetGeom);
        if (multiLineString) {
          let result:ISplitGeom = this.polygonize(multiLineString, jstsTargetGeom, jstsSplitterGeom);
          let targetArea:number = target.get('area_sqm');
          if (!targetArea || targetArea==0) {
            let tg = target.getGeometry();
            if (tg instanceof Polygon || tg instanceof MultiPolygon) {
              targetArea = this.$map.calcArea(tg);
              target.set('area_sqm',targetArea);
            }
          }
          let sqm, sumSplitsArea = 0;
          let allSplits = [];
          [result.splits, result.splits2].forEach((splits, i) => {
            splits.forEach((split,j) => {
              let f = target.clone();
              if (target['NATIVE_SOURCE']) {
                f['NATIVE_SOURCE'] = target['NATIVE_SOURCE'];
              }
              f.setGeometry(split);
              sqm = this.$map.calcArea(<Polygon>split);
              f.set('area_sqm', sqm);
              sumSplitsArea += sqm;
              if (i==0) {
                splitResult.splits.push({
                  original: target,
                  modified: f
                });
              } else {
                splitResult.splits2.push({
                  original: target,
                  modified: f
                });
              }
              allSplits.push(f);
            });
          });
          let delta = targetArea - sumSplitsArea;
          if (delta!==0) {
            // adjust split's area so that the sumArea of all splits is equal to targetArea
            let sumSplitsArea2 = 0;
            let all = allSplits.length-1;
            allSplits.forEach((s,i) => {
              if (i<all) {
                sqm = s.get('area_sqm');
                sqm += (sqm / sumSplitsArea) * delta;
                s.set('area_sqm', sqm);
                sumSplitsArea2 += sqm;
              } else {
                // this is the last split
                sqm = targetArea - sumSplitsArea2;
                s.set('area_sqm', sqm);
              }
            });
          }
        }
      }, this);

      if (splitResult.splits.length==0) {
        splitResult.splits = null;
      }
      if (splitResult.splits2.length==0) {
        splitResult.splits2 = null;
      }
      if (splitResult.splits || splitResult.splits2) {
        this.afterSplitted(splitResult);
      }

      // finally empty this.targets_ in order to continue to select and split others
      this.targets = null;
    } catch (error) {
      this.targets = null;
      this.$map.$emit('error', error);
    }
  }

  unionMultiLineStrings(jstsSplitterLS, jstsTargetGeom) : jsts.geom.MultiLineString {
    let jstsTargetEnvelope = jstsTargetGeom.getEnvelope();
    let jstsSplitterEnvelope = jstsSplitterLS.getEnvelope();
    if (!jstsSplitterEnvelope.intersects(jstsTargetEnvelope)) {
      return null;
    }

    let targetMultiLineString: jsts.geom.MultiLineString;
    let boundary = jstsTargetGeom.getBoundary();
    let factory = boundary.getFactory();
    if(boundary instanceof jsts.geom.LineString){
      targetMultiLineString = new jsts.geom.MultiLineString([boundary], factory);
    }
    else if (boundary instanceof jsts.geom.MultiLineString) {
      targetMultiLineString = boundary;
    }
    if (targetMultiLineString) {
      targetMultiLineString = targetMultiLineString.union(jstsSplitterLS);
    }
    return targetMultiLineString;
  }

  polygonize(unionMultiLineString, jstsTargetGeom, jstsSplitterGeom) : ISplitGeom {
    let polygonizer = new jsts.operation.polygonize.Polygonizer();
    /* jsts has bug. find following line and correct it
    class PolygonizeDirectedEdge extends DirectedEdge$1 {
      constructor() {
        super(...arguments); // changed from super()
        PolygonizeDirectedEdge.constructor_.apply(this, arguments);
      }
    */
    polygonizer.add(unionMultiLineString);
    let jstsPolygons = polygonizer.getPolygons();
    let result:ISplitGeom = {
      splits: [],
      splits2: []
    }
    for (let i = jstsPolygons.iterator(); i.hasNext();) {
      let polygon = i.next();
      if (this.partOf(polygon,jstsTargetGeom)) {
        // excluding those formed by part of target boundary and split boundary
        if (jstsSplitterGeom instanceof jsts.geom.Polygon) {
          if (this.partOf(polygon,jstsSplitterGeom)) {
            result.splits2.push(this.jstsOL3Parser.write(polygon));
          } else {
            result.splits.push(this.jstsOL3Parser.write(polygon));
          }
        } else {
          result.splits.push(this.jstsOL3Parser.write(polygon));
        }
      }
    }
    return result;
  }

  partOf(jstsGeom1,jstsGeom2) : boolean {
    let yes:boolean = true;
    let jstsGeomFactory = new jsts.geom.GeometryFactory();
    let coordinates = jstsGeom1.getCoordinates();
    let i, jstsPoint;
    for (i=0; i<coordinates.length-1; i++) {
      jstsPoint = jstsGeomFactory.createPoint(coordinates[i]);
      if (jstsPoint.distance(jstsGeom2)>0.01) {
        yes = false;
        break;
      }
    }
    if (yes) {
      // excluding those holes
      let intersection = jstsGeom1.intersection(jstsGeom2);
      if (intersection) {
        let iarea = intersection.getArea();
        yes = (iarea>0.01);
      }
    }
    return yes;
  }

  afterSplitted(split:ISplitResult) {
    let source:VectorSource;
    let results = (split.splits || []).concat(split.splits2 || []);
    if (results.length>0) {
      let hasUndoTarget:boolean = false;
      split.targets.forEach((target,index) => {
        source = target['NATIVE_SOURCE'];
        if (source) {
          this.$map.addUndo(target, 'Delete', index>0);
          hasUndoTarget = true;
        }
      });
      results.forEach((result,index) => {
        source = result.modified['NATIVE_SOURCE'];
        if (source) {
          this.$map.addUndo(result.modified, 'Insert', hasUndoTarget || index>0); //append
          source.addFeature(result.modified);
        }
      });
    }
  }

  /**
   * override Drawer.startDraw()
   */
  startDraw(event) {
    if (!this.hasTargets) {
      this.selectSplitTargets();
      this['abortDrawing_']();
      return;
    }
    //super.startDraw(event);
    this.sketch = event.feature;
    let geom = this.sketch.getGeometry();
    if (!(geom instanceof Point)) {
      let mapElem:Element = this.map.getTargetElement();
      mapElem.addEventListener('keyup', this.undoRedo_);
    }
  }

  /**
   * override Drawer.endDraw()
   */
  endDraw(event) {
    if (this.sketch) {
      // this.sketch is null endDraw() is called from this.remove()
      let geom = this.sketch.getGeometry();
      if (!(geom instanceof Point)) {
        let mapElem = this.map.getTargetElement();
        mapElem.removeEventListener('keyup', this.undoRedo_);
      }
    }
    this.$map.clearLayer('SelectedFeatureLayer');
    this.sketch = null;
    let splitter = event.feature;
    if (splitter && this.jstsOL3Parser) {
      this.startSplit(splitter);
    }
  }

  setMap(map:Map) {
    if (map) {
      this.$map = map['SOFTOO_MAP'];
    }
    super.setMap(map);
  }

  remove() {
    this.jstsOL3Parser = null;
    super.remove();
  }
}